import { createRoute, useSearch } from "@tanstack/solid-router";
import { useNavigate } from "@/lib/u-router";
import { isDateKey, MEAL_TYPES, type MealType } from "@mealplan/shared";
import { appRoute } from "./route";
import { PlannerView } from "@/features/planner/components/planner-view";
import { PageContainer } from "@/components/ui/page-container";
import { AppErrorBoundary } from "@/components/error-boundary";

interface PlannerSearch {
  week?: string;
  /** Día del picker "elegir receta" (`?add=YYYY-MM-DD`). */
  add?: string;
  /** Comida del picker (`?meal=breakfast|lunch|dinner|snack`). */
  meal?: MealType;
  /** Búsqueda dentro del picker (`?addq=`). */
  addq?: string;
}

const isMealType = (value: unknown): value is MealType =>
  typeof value === "string" && (MEAL_TYPES as readonly string[]).includes(value);

function PlannerPage() {
  const search = useSearch({ from: "/u/$u/planner" });
  const navigate = useNavigate();

  // Persiste la semana activa en la URL (deep-link `?week=YYYY-MM-DD`). `replace`
  // evita inflar el historial al paginar de semana en semana.
  const syncWeek = (weekStartKey: string): void => {
    if (weekStartKey === (search().week ?? "")) {
      return;
    }
    void navigate({
      to: "/planner",
      // Cambiar de semana cierra cualquier picker abierto (su día ya no aplica).
      search: { week: weekStartKey },
      replace: true,
    });
  };

  // El picker "elegir receta" es deep-linkable: `?add=<date>&meal=<meal>` abre el
  // diálogo en la celda exacta; `?addq=` preserva la búsqueda. Idempotente.
  const syncPicker = (picker: { date: string; meal: MealType } | null, query: string): void => {
    const current = search();
    const nextSearch = {
      ...(current.week ? { week: current.week } : {}),
      ...(picker ? { add: picker.date, meal: picker.meal } : {}),
      ...(picker && query.trim() ? { addq: query.trim() } : {}),
    } satisfies PlannerSearch;
    if (
      nextSearch.add === current.add &&
      nextSearch.meal === current.meal &&
      (nextSearch.addq ?? "") === (current.addq ?? "")
    ) {
      return;
    }
    void navigate({
      to: "/planner",
      search: nextSearch,
      replace: true,
    });
  };

  return (
    <PageContainer maxWidth="5xl">
      <AppErrorBoundary variant="page" region="planner">
        <PlannerView
          initialWeek={search().week}
          onWeekChange={syncWeek}
          pickerDate={search().add}
          pickerMeal={search().meal}
          pickerQuery={search().addq}
          onPickerChange={syncPicker}
        />
      </AppErrorBoundary>
    </PageContainer>
  );
}

export const plannerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planner",
  validateSearch: (search: Record<string, unknown>): PlannerSearch => ({
    week: typeof search.week === "string" && isDateKey(search.week) ? search.week : undefined,
    add: typeof search.add === "string" && isDateKey(search.add) ? search.add : undefined,
    meal: isMealType(search.meal) ? search.meal : undefined,
    addq: typeof search.addq === "string" && search.addq.trim() !== "" ? search.addq : undefined,
  }),
  component: PlannerPage,
});
